import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import { Star, Heart, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCurrency } from '@/lib/currency-context';
import { useCart } from '@/lib/cart-context';
import { useWishlist } from '@/lib/wishlist-context';
import { useAuth } from '@/lib/auth-context';
import { toast } from 'sonner';
import { ProductThumbnail } from '@/components/products/ProductThumbnail';
import { buildStorePath, getStoreSlugFromProduct } from '@/lib/store-url';
import { buildProductPath } from '@/lib/product-url';
import { productNeedsVariant } from '@/lib/variant';
import { trackAiEvent } from '@/lib/ai-search-track';
import { shouldBlurSensitiveImage } from '@/lib/sensitive-content';

// Helper function to get country flag emoji
const getCountryFlag = (countryCode) => {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

export const ProductCard = ({ product, variant = 'default', compact = false, onImageError }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const { addItem, items } = useCart();
  const { formatPrice } = useCurrency();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Get translated product name and description - using i18nInstance for reactivity
  const currentLang = (i18nInstance.language || 'tr').substring(0, 2);
  const translations = product.translations || {};
  const productName = translations[currentLang]?.name || product.name;
  const productDescription = translations[currentLang]?.description || product.description;

  const needsVariant = productNeedsVariant(product);
  const sensitiveImage = shouldBlurSensitiveImage(product);
  const isInCart = !needsVariant && items.some(item => item.product_id === product.id);
  const isProductInWishlist = isInWishlist(product.id);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if ((product.stock ?? 0) <= 0) {
      toast.error(t('product.outOfStock'));
      return;
    }
    if (needsVariant) {
      navigate(buildProductPath(i18n.language, product));
      return;
    }
    addItem(product, 1);
    trackAiEvent('add_to_cart', product.id);
    toast.success(t('cart.addedToCart'), {
      description: productName,
      action: {
        label: t('cart.goToCart') || 'Sepete Git',
        onClick: () => navigate('/cart'),
      },
      duration: 5000,
    });
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error(t('wishlist.loginRequired') || 'Favorilere eklemek için giriş yapmalısınız');
      navigate('/login');
      return;
    }
    
    await toggleWishlist(product.id, productName);
  };

  const handleGoToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/cart');
  };

  const discount = product.compare_price 
    ? Math.round((1 - product.price / product.compare_price) * 100) 
    : 0;

  // Compact mode for grid listings (Trendyol style) - daha küçük ve kompakt
  if (compact) {
    // Check if product is from Nevresim Dünyası seller (should show Made in Turkey badge)
    const isNevresimDunyasi = product.seller_info?.store_name?.toLowerCase()?.includes('nevresim') ||
                              product.seller_name?.toLowerCase()?.includes('nevresim');
    
    // Admin tarafından ayarlanan badge bilgileri
    const showMadeInTurkey = product.made_in_turkey || isNevresimDunyasi;
    const adminCountryFlag = product.country_flag;
    
    // Get image URL with optimization for mobile
    let rawImageUrl = product.images?.[0] || '';
    if (rawImageUrl.startsWith('/uploads/')) rawImageUrl = rawImageUrl.replace('/uploads/', '/api/uploads/');
    // Add width parameter for optimization if not present
    let imageUrl = rawImageUrl;
    if (rawImageUrl && !rawImageUrl.includes('w=')) {
      if (rawImageUrl.includes('unsplash.com')) {
        imageUrl = rawImageUrl + (rawImageUrl.includes('?') ? '&' : '?') + 'w=300&q=75';
      } else if (rawImageUrl.includes('pexels.com')) {
        imageUrl = rawImageUrl + (rawImageUrl.includes('?') ? '&' : '?') + 'w=300';
      }
    }
    
    return (
      <Link 
        to={buildProductPath(i18n.language, product)} 
        className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all"
        data-testid={`product-card-${product.id}`}
      >
        <div className="relative aspect-square overflow-hidden bg-gray-100">
          <ProductThumbnail
            src={imageUrl || ''}
            alt={productName}
            className="w-full h-full"
            coverImgClassName="group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            sensitive={sensitiveImage}
            onUnavailable={onImageError}
          />
          
          {/* Admin tarafından eklenen bayrak - sol üst */}
          {adminCountryFlag && (
            <div className="absolute top-1 left-1 z-10">
              <span className="text-lg drop-shadow-md" title={adminCountryFlag}>
                {getCountryFlag(adminCountryFlag)}
              </span>
            </div>
          )}
          
          {/* Seller country flag - only show if no admin flag (top left) */}
          {!adminCountryFlag && product.seller_info?.country && (
            <div className="absolute top-1 left-1 z-10 flex items-center gap-0.5 bg-white/90 backdrop-blur-sm px-1 py-0.5 rounded-full shadow-sm">
              <span className="text-xs">{getCountryFlag(product.seller_info.country)}</span>
            </div>
          )}
          
          <button 
            className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${isProductInWishlist ? 'bg-red-500 hover:bg-red-600' : 'bg-white/80 hover:bg-white'}`}
            onClick={handleToggleWishlist}
            data-testid={`wishlist-btn-${product.id}`}
          >
            <Heart className={`w-3 h-3 ${isProductInWishlist ? 'text-white fill-white' : 'text-gray-600'}`} />
          </button>
          
          {/* Made in Turkey badge - sağ alt */}
          {showMadeInTurkey && (
            <img 
              src="https://customer-assets.emergentagent.com/job_emarket-turkey-1/artifacts/dupde9ke_madeinturkiye%20logo%20.png"
              alt="Made in Türkiye"
              className="absolute bottom-1 right-1 w-6 h-6 object-contain drop-shadow-lg"
            />
          )}
        </div>
        <div className="p-2">
          <h3 className="text-xs font-medium text-gray-900 line-clamp-2 min-h-[2rem] leading-tight">{productName}</h3>
          {(product.rating > 0 && (product.review_count > 0 || product.reviews_count > 0)) && (
            <div className="flex items-center gap-1 mt-0.5">
              <Star className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" />
              <span className="text-[10px] text-gray-500">{product.rating}</span>
              <span className="text-[10px] text-gray-400">
                ({product.reviews_count || product.review_count})
              </span>
            </div>
          )}
          <div className="flex items-center gap-1 mt-1">
            <span className="font-bold text-sm text-red-600">{formatPrice(product.price, product.currency || 'TRY')}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    let compactImg = product.images?.[0] || '';
    if (compactImg.startsWith('/uploads/')) compactImg = compactImg.replace('/uploads/', '/api/uploads/');
    return (
      <Link 
        to={buildProductPath(i18n.language, product)} 
        className="group flex gap-4 p-3 rounded-xl hover:bg-secondary transition-colors"
        data-testid={`product-card-${product.id}`}
      >
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-secondary relative">
          <ProductThumbnail
            src={compactImg}
            alt={productName}
            className="w-full h-full"
            coverImgClassName="transition-opacity"
            loading="lazy"
            sensitive={sensitiveImage}
            onUnavailable={onImageError}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate">{productName}</h4>
          <div className="flex items-center gap-1 mt-1">
            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
            <span className="text-xs text-muted-foreground">{product.rating}</span>
          </div>
          <p className="font-semibold text-sm mt-1">{formatPrice(product.price, product.currency || 'TRY')}</p>
        </div>
      </Link>
    );
  }

  // Get image URL with optimization - kırık URL ProductThumbnail ile logo gösterir
  let defaultImageUrl = product.images?.[0] || '';
  if (defaultImageUrl.startsWith('/uploads/')) defaultImageUrl = defaultImageUrl.replace('/uploads/', '/api/uploads/');
  // Optimize image URL for performance
  if (defaultImageUrl && !defaultImageUrl.includes('w=')) {
    if (defaultImageUrl.includes('unsplash.com')) {
      defaultImageUrl = defaultImageUrl + (defaultImageUrl.includes('?') ? '&' : '?') + 'w=400&q=75';
    } else if (defaultImageUrl.includes('pexels.com')) {
      defaultImageUrl = defaultImageUrl + (defaultImageUrl.includes('?') ? '&' : '?') + 'w=400';
    }
  }

  return (
    <Link 
      to={buildProductPath(i18n.language, product)}
      className="group block"
      data-testid={`product-card-${product.id}`}
    >
      <div className="product-card rounded-2xl overflow-hidden bg-white border border-transparent hover:border-border hover:shadow-lg transition-all duration-300">
        {/* Image */}
        <div className="relative aspect-square bg-secondary overflow-hidden">
          <ProductThumbnail
            src={defaultImageUrl}
            alt={productName}
            className="w-full h-full"
            coverImgClassName="transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            sensitive={sensitiveImage}
            onUnavailable={onImageError}
          />
          
          {/* Seller country flag - top left corner */}
          {product.seller_info?.country && (
            <div className="absolute top-3 left-3 z-20 flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full shadow-md">
              <span className="text-base">{getCountryFlag(product.seller_info.country)}</span>
              <span className="text-xs font-medium text-gray-700">{product.seller_info.country}</span>
            </div>
          )}
          
          {/* Badges - Only show featured badge, no discount */}
          <div className={`absolute ${product.seller_info?.country ? 'top-12' : 'top-3'} left-3 flex flex-col gap-2`}>
            {product.is_featured && (
              <Badge className="bg-primary text-white border-0">
                {t('product.featured')}
              </Badge>
            )}
            {/* Made in Turkey Badge */}
            {product.made_in_turkey && (
              <div className="flex items-center gap-1 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-md">
                <span>🇹🇷</span>
                <span>Made in Türkiye</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button 
              size="icon" 
              variant={isProductInWishlist ? "default" : "secondary"}
              className={`w-9 h-9 rounded-full shadow-md transition-colors ${isProductInWishlist ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
              onClick={handleToggleWishlist}
              data-testid={`wishlist-btn-${product.id}`}
            >
              <Heart className={`w-4 h-4 ${isProductInWishlist ? 'fill-current' : ''}`} />
            </Button>
          </div>

          {/* Quick add */}
          <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
            {isInCart ? (
              <Button 
                onClick={handleGoToCart}
                className="w-full rounded-full shadow-lg bg-green-600 hover:bg-green-700"
                data-testid={`go-to-cart-${product.id}`}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {t('cart.goToCart') || 'Sepete Git'}
              </Button>
            ) : (
              <Button 
                onClick={handleAddToCart}
                disabled={(product.stock ?? 0) <= 0}
                className="w-full rounded-full shadow-lg"
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {(product.stock ?? 0) <= 0 ? t('product.outOfStock') : t('product.addToCart')}
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-1">
            {product.brand && (
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                {product.brand}
              </p>
            )}
            {product.origin_country && (
              <span className="text-xs font-medium flex items-center gap-1">
                {getCountryFlag(product.origin_country)} {product.origin_country}
              </span>
            )}
          </div>
          <h3 className="font-medium text-foreground line-clamp-2 min-h-[2.5rem]">
            {productName}
          </h3>
          
          {/* Seller info with flag */}
          {(product.seller_info?.store_name || product.seller_name) && (() => {
            const storeName = product.seller_info?.store_name || product.seller_name;
            const storeSlug = getStoreSlugFromProduct(product);
            const langCode = (i18nInstance.language || 'tr').substring(0, 2);
            const storeHref = storeSlug ? buildStorePath(langCode, storeSlug) : null;
            return (
              <div className="flex items-center gap-1 mt-1">
                {product.seller_info?.country && (
                  <span className="text-sm">{getCountryFlag(product.seller_info.country)}</span>
                )}
                {storeHref ? (
                  <Link
                    to={storeHref}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-muted-foreground hover:text-primary hover:underline truncate"
                  >
                    {storeName}
                  </Link>
                ) : (
                  <span className="text-xs text-muted-foreground truncate">{storeName}</span>
                )}
              </div>
            );
          })()}
          
          {product.rating > 0 && product.review_count > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-3.5 h-3.5 ${
                      star <= Math.round(product.rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'fill-gray-200 text-gray-200'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-muted-foreground">({product.review_count})</span>
            </div>
          )}

          {/* Price - only current price, no compare price */}
          <div className="flex items-baseline gap-2 mt-3">
            <span className="text-lg font-bold text-foreground">
              {formatPrice(product.price, product.currency || 'TRY')}
            </span>
          </div>

          {/* Stock */}
          {(product.stock ?? 0) <= 0 && (
            <p className="text-xs text-red-600 mt-2 font-medium">
              {t('product.outOfStock')}
            </p>
          )}
          {product.stock < 10 && product.stock > 0 && (
            <p className="text-xs text-accent mt-2 font-medium">
              {t('product.onlyLeftInStock', { count: product.stock })}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
};
